import {useMemo, type FC} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'
import {BranchFilterProvider} from './providers/branch'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {RulesetFilterProvider} from './providers/ruleset'
import {UserFilterProvider} from './providers/user'
import {RuleStatusFilterProvider} from './providers/rule-status'
import {ShaFilterProvider} from './providers/sha'
import {
  SecurityProductsFilterBar,
  parseAsUser,
  serializeLogin,
  serializeName,
  filterDefinition,
  timePeriodFilterDefinition,
  repositoryFilterDefinition,
  organizationFilterDefinition,
  type FilterValue,
  type FilterDefinition,
} from './SecurityProductsFilterBar'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useIsStafftools} from '../../hooks/use-is-stafftools'
import {useInsightsActors} from '../../hooks/use-insights-actors'

type InsightsFilterBar2Props = {
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  businessSlug?: string
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  filter,
  source,
  sourceType,
  businessSlug,
  rulesets,
}) => {
  const rulesInsightsFilterBarSha = useFeatureFlag('rules_insights_filter_bar_sha')
  const rulesInsightsFilterBarShaOrgAndBiz = useFeatureFlag('rules_insights_filter_bar_sha_org_and_biz')
  const isStafftools = useIsStafftools()
  const isRepoNonStafftools = sourceType === 'repository' && !isStafftools
  const showShaFilter =
    (sourceType === 'repository' && rulesInsightsFilterBarSha) ||
    ((sourceType === 'organization' || sourceType === 'enterprise') && rulesInsightsFilterBarShaOrgAndBiz)
  const {users: actors} = useInsightsActors()

  const filterDefinitions = useMemo<Array<FilterDefinition<InsightsFilter>>>(
    () => [
      timePeriodFilterDefinition<InsightsFilter>(),
      filterDefinition<InsightsFilter>({
        config: {
          filterKey: 'evaluateStatus',
          queryKey: 'evaluate-status',
          fallback: 'all',
        },
        provider: new EvaluateStatusFilterProvider(),
      }),
      ...(isRepoNonStafftools
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'branch', queryKey: 'branch'},
              provider: new BranchFilterProvider((source as Repository).ownerLogin, (source as Repository).name),
            }),
          ]
        : []),
      ...(sourceType === 'organization' ? [repositoryFilterDefinition<InsightsFilter>(source.name)] : []),
      ...(sourceType === 'enterprise' && businessSlug
        ? [organizationFilterDefinition<InsightsFilter>(businessSlug)]
        : []),
      filterDefinition<InsightsFilter>({
        config: {
          filterKey: 'ruleset',
          queryKey: 'ruleset',
          parse: (values: FilterValue[]) => rulesets.find(r => r.name === values[0]?.displayName),
          serialize: (v: unknown) => serializeName(v as {name: string}),
        },
        provider: new RulesetFilterProvider(rulesets),
      }),
      filterDefinition<InsightsFilter>({
        config: {
          filterKey: 'actor',
          queryKey: 'user',
          parse: parseAsUser,
          serialize: (v: unknown) => serializeLogin(v as {login: string}),
        },
        provider: new UserFilterProvider(actors),
      }),
      filterDefinition<InsightsFilter>({
        config: {filterKey: 'ruleStatus', queryKey: 'rule-status'},
        provider: new RuleStatusFilterProvider(),
      }),
      ...(showShaFilter
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'afterOid', queryKey: 'sha'},
              provider: new ShaFilterProvider(),
            }),
          ]
        : []),
    ],
    [rulesets, sourceType, showShaFilter, businessSlug, source, isRepoNonStafftools, actors],
  )

  return (
    <SecurityProductsFilterBar<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      filterDefinitions={filterDefinitions}
      getIndexPath={insightsIndexPath}
    />
  )
}
